import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import kotlinx.coroutines.launch

// Turnaje: jako tréninky, ale navíc název, výchozí typ bowlingu na úrovni turnaje (default pro
// všechny hry, per-sloupec přepínatelný přes ⚙) a volitelné umístění hráčů (pořadí).

data class TournamentEdit(
    val tournament: SgTournamentDto,
    val scores: List<SgScoreDto>,
    val sheets: List<SgScoresheetDto>,
    val placements: List<SgPlacementDto>,
)

@Composable
fun TournamentForm(
    token: String,
    member: SgMemberDto?,
    profiles: List<SgProfileDto>,
    editing: TournamentEdit?,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = editing != null
    var name by remember { mutableStateOf(editing?.tournament?.name ?: "") }
    var date by remember { mutableStateOf(editing?.tournament?.date ?: kotlin.js.Date().toISOString().substring(0, 10)) }
    var note by remember { mutableStateOf(editing?.tournament?.notes ?: "") }
    var tourType by remember { mutableStateOf(editing?.tournament?.gameType ?: "classic") }
    var games by remember { mutableStateOf(editing?.scores?.maxOfOrNull { it.gameNo } ?: 3) }
    val scores = remember { mutableStateMapOf<String, String>().apply { editing?.scores?.forEach { put("${it.blPlayerId}#${it.gameNo}", it.score.toString()) } } }
    // per-sloupec override typu (jinak se použije tourType)
    val gameTypes = remember { mutableStateMapOf<Int, String>().apply { editing?.scores?.forEach { put(it.gameNo, it.gameType) } } }
    val sheets = remember { mutableStateMapOf<String, List<FrameDto>>().apply { editing?.sheets?.forEach { put("${it.blPlayerId}#${it.gameNo}", it.frames) } } }
    val placements = remember { mutableStateMapOf<String, String>().apply { editing?.placements?.forEach { put(it.blPlayerId, it.placement.toString()) } } }
    var showTypes by remember { mutableStateOf(false) }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isEdit) "Úprava turnaje" else "Nový turnaj") }
        Div(attrs = { classes("training-meta") }) {
            Label { Text("Název"); Input(type = InputType.Text) { classes("field"); placeholder("název turnaje"); value(name); onInput { name = it.value } } }
            Label { Text("Datum"); Input(type = InputType.Date) { classes("field"); value(date); onInput { date = it.value } } }
            Label {
                Text("Typ")
                Select(attrs = { classes("field"); onChange { ev -> ev.value?.let { tourType = it } } }) {
                    BOWLING_TYPES.forEach { (code, label) ->
                        Option(code, attrs = { if (tourType == code) attr("selected", "selected") }) { Text(label) }
                    }
                }
            }
            Label { Text("Poznámka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(note); onInput { note = it.value } } }
            Div(attrs = { classes("game-controls") }) {
                Button(attrs = { classes("small-btn"); onClick { games++ } }) { Text("+ přidat hru") }
                Button(attrs = { classes("small-btn"); if (games <= 1) disabled(); onClick { if (games > 1) games-- } }) { Text("− odebrat hru") }
                Button(attrs = {
                    classes(*(listOfNotNull("small-btn", "icon-btn", "small-btn-on".takeIf { showTypes }).toTypedArray()))
                    attr("title", "Typy her"); attr("aria-label", "Typy her")
                    onClick { showTypes = !showTypes }
                }) { Text("⚙") }
            }
        }

        ScoreEntryTable(
            profiles = profiles,
            games = games,
            showTypes = showTypes,
            gameTypes = gameTypes,
            defaultType = tourType,
            scores = scores,
            sheets = sheets,
            onOpenEditor = { key, type -> editorTarget = EditorTarget(key, type) },
            headExtra = { Text("Pořadí") },
            rowExtra = { p ->
                Input(type = InputType.Text) {
                    classes("placement-input"); attr("inputmode", "numeric"); placeholder("–")
                    value(placements[p.blPlayerId] ?: "")
                    onInput { placements[p.blPlayerId] = it.value }
                }
            },
        )

        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                    if (createdBy == null) { message = "Chybí účet člena."; return@onClick }
                    if (name.isBlank()) { message = "Zadej název turnaje."; return@onClick }
                    val entries = profiles.flatMap { p ->
                        (1..games).mapNotNull { g ->
                            scores["${p.blPlayerId}#$g"]?.trim()?.toIntOrNull()?.let { ScoreEntry(p.blPlayerId, g, it, gameTypes[g] ?: tourType) }
                        }
                    }
                    if (entries.isEmpty()) { message = "Zadej aspoň jedno skóre."; return@onClick }
                    val sheetEntries = sheets.mapNotNull { (key, frames) ->
                        val parts = key.split("#")
                        val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                        if (g > games) return@mapNotNull null
                        val type = gameTypes[g] ?: tourType
                        SheetEntry(parts[0], g, type, frames, Scoring.total(type, frames.map { it.t }))
                    }
                    val placementEntries = placements.mapNotNull { (pid, v) ->
                        v.trim().toIntOrNull()?.takeIf { it >= 1 }?.let { PlacementEntry(pid, it) }
                    }
                    busy = true; message = null
                    scope.launch {
                        try {
                            if (isEdit) {
                                val id = editing!!.tournament.id
                                Api.updateTournament(token, id, name, date, tourType, note)
                                Api.deleteTournamentScores(token, id); Api.addTournamentScores(token, id, entries)
                                Api.deleteTournamentSheets(token, id); Api.addTournamentSheets(token, id, createdBy, sheetEntries)
                                Api.deleteTournamentPlacements(token, id); Api.addTournamentPlacements(token, id, placementEntries)
                            } else {
                                val id = Api.createTournament(token, createdBy, name, date, tourType, note)
                                Api.addTournamentScores(token, id, entries)
                                Api.addTournamentSheets(token, id, createdBy, sheetEntries)
                                Api.addTournamentPlacements(token, id, placementEntries)
                                scores.clear(); sheets.clear(); placements.clear(); name = ""; note = ""
                            }
                            onSaved()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally {
                            busy = false
                        }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (isEdit) "Uložit změny" else "Uložit turnaj") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
        }
        message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }

        editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = target.gameType,
                initialFrames = sheets[target.key] ?: emptyList(),
                photoImportEnabled = true,
                gameLabel = "${profiles.find { it.blPlayerId == target.key.substringBefore('#') }?.displayName ?: "Hráč"} · hra ${target.key.substringAfter('#')}",
                expectedScore = scores[target.key]?.toIntOrNull(),
                onSave = { frames, total ->
                    sheets[target.key] = frames
                    scores[target.key] = total.toString()
                    editorTarget = null
                },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
fun TournamentsList(
    tournaments: List<SgTournamentDto>,
    scoresByTournament: Map<String, List<SgScoreDto>>,
    placementsByTournament: Map<String, List<SgPlacementDto>>,
    profiles: List<SgProfileDto>,
    readOnly: Boolean = false,
    onEdit: (SgTournamentDto) -> Unit,
    onDelete: (SgTournamentDto) -> Unit,
) {
    val nameById = remember(profiles) { profiles.associate { it.blPlayerId to it.displayName } }
    Div(attrs = { classes("card") }) {
        H3 { Text("Odehrané turnaje") }
        if (tournaments.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné turnaje.") }
            return@Div
        }
        Div(attrs = { classes("training-list") }) {
            tournaments.forEach { t ->
                val scores = scoresByTournament[t.id] ?: emptyList()
                val placements = (placementsByTournament[t.id] ?: emptyList()).sortedBy { it.placement }
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            Span(attrs = { classes("training-date") }) { Text(t.name) }
                            Span(attrs = { classes("muted") }) { Text(" · ${t.date} · ${bowlingTypeLabel(t.gameType)}") }
                            t.notes?.let { Span(attrs = { classes("muted") }) { Text(" · $it") } }
                        }
                        if (!readOnly) {
                            Div(attrs = { classes("training-actions") }) {
                                Button(attrs = { classes("small-btn"); onClick { onEdit(t) } }) { Text("Upravit") }
                                Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete(t) } }) { Text("Smazat") }
                            }
                        }
                    }
                    if (placements.isNotEmpty()) {
                        Div(attrs = { classes("training-players") }) {
                            placements.forEach { pl ->
                                Span(attrs = { classes("placement-chip") }) { Text("${pl.placement}. ${nameById[pl.blPlayerId] ?: pl.blPlayerId}") }
                            }
                        }
                    }
                    val byPlayer = scores.groupBy { it.blPlayerId }
                    Div(attrs = { classes("training-players") }) {
                        byPlayer.entries.sortedByDescending { it.value.map { s -> s.score }.average() }.forEach { (pid, sc) ->
                            val avg = (sc.map { it.score }.average() * 10).toInt() / 10.0
                            Span(attrs = { classes("training-player") }) { Text("${nameById[pid] ?: pid}: Ø $avg (${sc.size})") }
                        }
                    }
                }
            }
        }
    }
}
